import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import { GitProvenanceResponse } from "common";

/**
 * The Provenance tab's two reads (git-4-2). Both answer 403 to anyone who may not write the
 * revision, so a 403 is final and not retried; the tab turns it into a sentence.
 */

export const processProvenanceKey = (
  processId: number
): (string | number)[] => ["/api/v1/process", processId, "git", "provenance"];

export const pipelineProvenanceKey = (
  pipelineId: number
): (string | number)[] => ["/api/v1/pipeline", pipelineId, "git", "provenance"];

export const isProvenanceForbidden = (error: unknown): boolean =>
  403 === (error as { response?: { status?: number } })?.response?.status;

export const useProcessProvenance = (
  processId: number | undefined
): UseQueryResult<GitProvenanceResponse, ErrorNotification> =>
  useQuery<GitProvenanceResponse, ErrorNotification>({
    queryKey: processProvenanceKey(processId ?? 0),
    queryFn: async () => {
      const res = await axios.get(
        `/api/v1/process/${processId}/git/provenance`
      );
      return res.data as GitProvenanceResponse;
    },
    enabled: undefined !== processId && processId > 0,
    retry: false,
  });

export const usePipelineProvenance = (
  pipelineId: number | undefined
): UseQueryResult<GitProvenanceResponse, ErrorNotification> =>
  useQuery<GitProvenanceResponse, ErrorNotification>({
    queryKey: pipelineProvenanceKey(pipelineId ?? 0),
    queryFn: async () => {
      const res = await axios.get(
        `/api/v1/pipeline/${pipelineId}/git/provenance`
      );
      return res.data as GitProvenanceResponse;
    },
    enabled: undefined !== pipelineId && pipelineId > 0,
    retry: false,
  });
